/**
 * UserRoleAssignmentPanel - User Role Assignment Management
 *
 * FERP CRITICAL: User role assignment with complete audit trail
 *
 * Features:
 * - View all user role assignments
 * - Assign roles to users
 * - Revoke role assignments
 * - Assignment expiration management
 * - Assignment history viewing
 * - Complete audit trail
 *
 * Architecture Principles:
 * - Data-driven (all assignments from RoleRepository)
 * - Backend ID generation (Snowflake IDs)
 * - Repository pattern for operations
 * - Immutable audit trails (revocation preserves history)
 * - Life-critical validation
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Table,
  Group,
  Text,
  Badge,
  Button,
  TextInput,
  Select,
  Modal,
  Textarea,
  Alert,
  Card,

  Stack,
  ActionIcon,
  Tooltip,
  Box,
  Code,
} from '@mantine/core';
import {
  IconUser,
  IconShield,
  IconTrash,
  IconEye,
  IconSearch,
  IconPlus,
  IconAlertCircle,
  IconCalendar,
} from '@tabler/icons-react';
import { DateTimePicker } from '@mantine/dates';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import {
  UserRoleAssignment,
  RoleDefinition,
  RoleRepository,
  AssignRoleInput,
  RevokeRoleInput,
} from '../data/repositories/RoleRepository';

export interface UserRoleAssignmentPanelProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId: string;
  /** Is loading */
  isLoading: boolean;
}

/**
 * User Role Assignment Panel
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (assignments from RoleRepository)
 * - ✅ Backend Snowflake ID generation
 * - ✅ Repository pattern
 * - ✅ Immutable audit trails (revocation preserves history)
 * - ✅ Life-critical validation
 */
export function UserRoleAssignmentPanel({
  tenantId,
  isLoading: _parentLoading,
}: UserRoleAssignmentPanelProps) {
  const apolloClient = useApolloClient() as ApolloClient;
  const repository = useMemo(() => new RoleRepository(apolloClient), [apolloClient]);

  // State
  const [assignments, setAssignments] = useState<UserRoleAssignment[]>([]);
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Search and filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRevoked, setFilterRevoked] = useState<string>('active'); // 'all' | 'active' | 'revoked'

  // Modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<UserRoleAssignment | null>(null);

  // Assign form state
  const [formUserId, setFormUserId] = useState('');
  const [formRoleId, setFormRoleId] = useState('');
  const [formExpiresAt, setFormExpiresAt] = useState<Date | string | null>(null);

  // Revoke form state
  const [formRevocationReason, setFormRevocationReason] = useState('');

  /**
   * Load assignments from backend
   * FERP: Repository pattern for all data access
   */
  const loadAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // TODO: Replace with actual user ID from auth context
      const userId = 'current-user-id';
      const assignmentList = await repository.getUserRoles(userId, tenantId);
      setAssignments(assignmentList ?? []);
    } catch (err) {
      console.error('[UserRoleAssignmentPanel] Failed to load assignments:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [tenantId, repository]);

  /**
   * Load available roles
   * FERP: Repository pattern for role list
   */
  const loadRoles = useCallback(async () => {
    try {
      const roleList = await repository.listRoles(tenantId);
      setRoles((roleList ?? []).filter((r) => r.enabled)); // Only show enabled roles
    } catch (err) {
      console.error('[UserRoleAssignmentPanel] Failed to load roles:', err);
    }
  }, [tenantId, repository]);

  // Load data on mount
  useEffect(() => {
    loadAssignments();
    loadRoles();
  }, [loadAssignments, loadRoles]);

  /**
   * Filter assignments based on search and filters
   * FERP: Client-side filtering for responsive UX
   */
  const filteredAssignments = useMemo(() => {
    let filtered = assignments ?? [];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (assignment) =>
          assignment.userId.toLowerCase().includes(query) ||
          assignment.roleId.toLowerCase().includes(query) ||
          assignment.role?.displayName.toLowerCase().includes(query)
      );
    }

    // Revoked filter
    if (filterRevoked === 'active') {
      filtered = filtered.filter((assignment) => !assignment.revoked);
    } else if (filterRevoked === 'revoked') {
      filtered = filtered.filter((assignment) => assignment.revoked);
    }

    return filtered;
  }, [assignments, searchQuery, filterRevoked]);

  /**
   * Handle view assignment details
   * FERP: Display assignment details modal
   */
  const handleViewAssignment = (assignment: UserRoleAssignment) => {
    setSelectedAssignment(assignment);
    setIsViewModalOpen(true);
  };

  /**
   * Handle assign role
   * FERP: Open assign modal with empty form
   */
  const handleAssignRole = () => {
    setFormUserId('');
    setFormRoleId('');
    setFormExpiresAt(null);
    setIsAssignModalOpen(true);
  };

  /**
   * Handle revoke role
   * FERP: Open revoke confirmation modal
   */
  const handleRevokeRole = (assignment: UserRoleAssignment) => {
    setSelectedAssignment(assignment);
    setFormRevocationReason('');
    setIsRevokeModalOpen(true);
  };

  /**
   * Submit assign role
   * FERP: Backend mutation with repository pattern
   */
  const handleSubmitAssign = async () => {
    if (!formUserId.trim() || !formRoleId.trim()) {
      return;
    }

    try {
      setLoading(true);
      const input: AssignRoleInput = {
        userId: formUserId.trim(),
        tenantId,
        roleId: formRoleId.trim(),
        expiresAt: formExpiresAt ? (formExpiresAt instanceof Date ? formExpiresAt.toISOString() : formExpiresAt) : undefined,
      };

      await repository.assignRole(input);
      await loadAssignments(); // Refresh list
      setIsAssignModalOpen(false);
    } catch (err) {
      console.error('[UserRoleAssignmentPanel] Failed to assign role:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Submit revoke role
   * FERP: Backend mutation with repository pattern (immutable audit)
   */
  const handleSubmitRevoke = async () => {
    if (!selectedAssignment) return;

    try {
      setLoading(true);
      const input: RevokeRoleInput = {
        userId: selectedAssignment.userId,
        tenantId,
        roleId: selectedAssignment.roleId,
        reason: formRevocationReason.trim() || 'No reason provided',
      };

      await repository.revokeRole(input);
      await loadAssignments(); // Refresh list
      setIsRevokeModalOpen(false);
    } catch (err) {
      console.error('[UserRoleAssignmentPanel] Failed to revoke role:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if assignment is expired
   * FERP: Expiration checking
   */
  const isExpired = (assignment: UserRoleAssignment): boolean => {
    if (!assignment.expiresAt) return false;
    return new Date(assignment.expiresAt) < new Date();
  };

  /**
   * Get assignment status badge
   * FERP: Visual status indicators
   */
  const getStatusBadge = (assignment: UserRoleAssignment) => {
    if (assignment.revoked) {
      return (
        <Badge color="red" size="sm">
          Revoked
        </Badge>
      );
    }
    if (isExpired(assignment)) {
      return (
        <Badge color="orange" size="sm">
          Expired
        </Badge>
      );
    }
    return (
      <Badge color="green" size="sm">
        Active
      </Badge>
    );
  };

  // Error state
  if (error) {
    return (
      <Alert color="red" title="Error Loading Assignments" icon={<IconAlertCircle size={16} />}>
        {error.message}
        <Button variant="light" size="xs" mt="xs" onClick={loadAssignments}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header Controls */}
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <TextInput
            placeholder="Search users or roles..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{ width: 300 }}
          />
          <Select
            placeholder="Filter by status"
            value={filterRevoked}
            onChange={(value) => setFilterRevoked(value || 'active')}
            data={[
              { value: 'active', label: 'Active Only' },
              { value: 'revoked', label: 'Revoked Only' },
              { value: 'all', label: 'All Assignments' },
            ]}
            style={{ width: 150 }}
          />
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={handleAssignRole}>
          Assign Role
        </Button>
      </Group>

      {/* Assignments Table */}
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>User</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th>Assigned By</Table.Th>
            <Table.Th>Assigned At</Table.Th>
            <Table.Th>Expires At</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filteredAssignments.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Text ta="center" c="dimmed" py="xl">
                  {searchQuery.trim() || filterRevoked !== 'active'
                    ? 'No assignments match your filters'
                    : 'No role assignments yet'}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {filteredAssignments.map((assignment) => (
            <Table.Tr key={assignment.assignmentId}>
              <Table.Td>
                <Group gap="xs">
                  <IconUser size={16} />
                  <Text size="sm" fw={600}>
                    {assignment.userId}
                  </Text>
                </Group>
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <IconShield size={16} />
                  <Text size="sm">{assignment.role?.displayName || assignment.roleId}</Text>
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {assignment.assignedBy}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {new Date(assignment.assignedAt).toLocaleDateString()}
                </Text>
              </Table.Td>
              <Table.Td>
                {assignment.expiresAt ? (
                  <Group gap="xs">
                    <IconCalendar size={14} />
                    <Text size="sm" c={isExpired(assignment) ? 'red' : 'dimmed'}>
                      {new Date(assignment.expiresAt).toLocaleDateString()}
                    </Text>
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed">
                    Never
                  </Text>
                )}
              </Table.Td>
              <Table.Td>{getStatusBadge(assignment)}</Table.Td>
              <Table.Td>
                <Group gap={4}>
                  <Tooltip label="View Details">
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      onClick={() => handleViewAssignment(assignment)}
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                  </Tooltip>
                  {!assignment.revoked && (
                    <Tooltip label="Revoke Assignment">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleRevokeRole(assignment)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {/* View Assignment Details Modal */}
      <Modal
        opened={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title={
          <Group gap="xs">
            <IconUser size={20} />
            <Text fw={700}>Assignment Details</Text>
          </Group>
        }
        size="lg"
      >
        {selectedAssignment && (
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text size="sm" c="dimmed">
                  Assignment ID
                </Text>
                <Code>{selectedAssignment.assignmentId}</Code>
              </div>
              {getStatusBadge(selectedAssignment)}
            </Group>

            <div>
              <Text size="sm" c="dimmed">
                User ID
              </Text>
              <Text fw={600}>{selectedAssignment.userId}</Text>
            </div>

            <div>
              <Text size="sm" c="dimmed">
                Role
              </Text>
              <Group gap="xs">
                <IconShield size={16} />
                <Text fw={600}>
                  {selectedAssignment.role?.displayName || selectedAssignment.roleId}
                </Text>
              </Group>
            </div>

            <div>
              <Text size="sm" c="dimmed">
                Assigned By
              </Text>
              <Text size="sm">{selectedAssignment.assignedBy}</Text>
            </div>

            <div>
              <Text size="sm" c="dimmed">
                Assigned At
              </Text>
              <Text size="sm">{new Date(selectedAssignment.assignedAt).toLocaleString()}</Text>
            </div>

            {selectedAssignment.expiresAt && (
              <div>
                <Text size="sm" c="dimmed">
                  Expires At
                </Text>
                <Text size="sm" c={isExpired(selectedAssignment) ? 'red' : undefined}>
                  {new Date(selectedAssignment.expiresAt).toLocaleString()}
                  {isExpired(selectedAssignment) && ' (EXPIRED)'}
                </Text>
              </div>
            )}

            {selectedAssignment.revoked && (
              <>
                <div>
                  <Text size="sm" c="dimmed">
                    Revoked By
                  </Text>
                  <Text size="sm" c="red">
                    {selectedAssignment.revokedBy}
                  </Text>
                </div>
                <div>
                  <Text size="sm" c="dimmed">
                    Revoked At
                  </Text>
                  <Text size="sm" c="red">
                    {selectedAssignment.revokedAt &&
                      new Date(selectedAssignment.revokedAt).toLocaleString()}
                  </Text>
                </div>
                {selectedAssignment.revocationReason && (
                  <div>
                    <Text size="sm" c="dimmed">
                      Revocation Reason
                    </Text>
                    <Text size="sm" c="red">
                      {selectedAssignment.revocationReason}
                    </Text>
                  </div>
                )}
              </>
            )}

            <Button fullWidth onClick={() => setIsViewModalOpen(false)}>
              Close
            </Button>
          </Stack>
        )}
      </Modal>

      {/* Assign Role Modal */}
      <Modal
        opened={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        title={
          <Group gap="xs">
            <IconShield size={20} />
            <Text fw={700}>Assign Role to User</Text>
          </Group>
        }
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="User ID"
            description="User identifier (email or username)"
            placeholder="user@example.com"
            value={formUserId}
            onChange={(e) => setFormUserId(e.currentTarget.value)}
            required
            leftSection={<IconUser size={16} />}
          />

          <Select
            label="Role"
            description="Role to assign to the user"
            placeholder="Select role"
            data={roles.map((r) => ({ value: r.roleId, label: r.displayName }))}
            value={formRoleId}
            onChange={(value) => setFormRoleId(value || '')}
            required
            leftSection={<IconShield size={16} />}
            searchable
          />

          <DateTimePicker
            label="Expires At (Optional)"
            description="When this assignment should expire"
            placeholder="Select expiration date"
            value={formExpiresAt}
            onChange={setFormExpiresAt}
            leftSection={<IconCalendar size={16} />}
            clearable
            minDate={new Date()}
          />

          <Group justify="space-between">
            <Button variant="subtle" onClick={() => setIsAssignModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAssign}
              disabled={!formUserId.trim() || !formRoleId.trim()}
              loading={loading}
            >
              Assign Role
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Revoke Role Modal */}
      <Modal
        opened={isRevokeModalOpen}
        onClose={() => setIsRevokeModalOpen(false)}
        title={
          <Group gap="xs">
            <IconTrash size={20} color="red" />
            <Text fw={700}>Revoke Role Assignment</Text>
          </Group>
        }
        size="md"
      >
        {selectedAssignment && (
          <Stack gap="md">
            <Alert color="orange" icon={<IconAlertCircle size={16} />}>
              Are you sure you want to revoke this role assignment? The user will immediately lose
              all permissions associated with this role.
            </Alert>

            <Card withBorder p="sm">
              <Text size="sm" c="dimmed">
                User: <strong>{selectedAssignment.userId}</strong>
              </Text>
              <Text size="sm" c="dimmed">
                Role: <strong>{selectedAssignment.role?.displayName || selectedAssignment.roleId}</strong>
              </Text>
              <Text size="sm" c="dimmed">
                Assigned: {new Date(selectedAssignment.assignedAt).toLocaleDateString()}
              </Text>
            </Card>

            <Textarea
              label="Revocation Reason"
              description="Why is this role being revoked?"
              placeholder="User changed departments"
              value={formRevocationReason}
              onChange={(e) => setFormRevocationReason(e.currentTarget.value)}
              minRows={3}
            />

            <Group justify="space-between">
              <Button variant="subtle" onClick={() => setIsRevokeModalOpen(false)}>
                Cancel
              </Button>
              <Button color="red" onClick={handleSubmitRevoke} loading={loading}>
                Revoke Role
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Box>
  );
}
